// Enums and Constants
import type { OptionalPageLimit, PaginationMeta } from './common';
import type { ApiResponse, GetListResponse } from './generic';
import type {
  CatchUpMethod,
  GrantRecipientScope,
  LeaveGrantType,
  ScheduledGrantFailure,
  ScheduledGrantStatus,
} from '@gokrew/models/leaves';
import type { LeaveCycleSettings } from '@gokrew/models/leave-cycles';
import type { ApprovalRoute } from '@gokrew/models/approvals';
import type {
  LeaveTracking,
  SupportingDocumentRule,
} from '@gokrew/models/leave-management';

export enum PolicyScope {
  All = 'all',
  DepartmentDesignation = 'department_designation',
  Manual = 'manual',
}

export enum AllocationType {
  one_time = 'one_time',
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  halfYearly = 'half_yearly',
  /** The yearly allowance, credited in twelve monthly parts. */
  Yearly = 'yearly',
}

export enum CarryForwardType {
  All = 'all',
  MaxLimit = 'max_limit',
  Percentage = 'percentage',
}

// Department-Designation Mapping Types

export interface DepartmentDesignationPair {
  departmentId: string;
  designationIds: string[];
}

export interface DepartmentDesignationMappings {
  pairs?: DepartmentDesignationPair[];
  departmentIds?: string[];
  designationIds?: string[];
}

export interface EmployeeMappings {
  employeeIds: string[];
}

// API response enriched mapping types (include nested name objects)
export interface DepartmentDesignationMappingItem {
  id: string;
  departmentId: string | null;
  designationId: string | null;
  department: { id: string; name: string } | null;
  designation: { id: string; name: string } | null;
}

export interface EmployeeLeaveMappingItem {
  id: string;
  employeeId: string;
  employee?: { id: string; firstName: string; lastName: string } | null;
}

// Branch mapping returned on a policy (which branches a policy applies to)
export interface CompanyLeavePolicyBranchMapping {
  id: string;
  companyId: string;
  leavePolicyId: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
}

// Leave Policy Types

export interface CompanyLeaveType {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PolicyLeaveTypeWithDetails = {
  id: string;
  companyId: string;
  companyLeavePolicyId: string;
  companyLeaveTypeId: string;
  allocationType: AllocationType;
  allowance: number;
  isProRated: boolean;
  isAutoApproved?: boolean;
  tracking?: LeaveTracking;
  isPaid?: boolean;
  reasonRequired?: boolean;
  /** false: an HR-only type (employees cannot request it). */
  employeeCanRequest?: boolean;
  supportingDocument?: SupportingDocumentRule;
  /** This rule's own approval route; null = the company route (D187). */
  approvalRoute?: ApprovalRoute | null;
  allocationDate: number;
  isCarryForwardEnabled: boolean;
  carryForwardType: CarryForwardType | null;
  carryForwardMaxDays: number | null;
  carryForwardPercentage: number | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  deletedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  companyLeaveType: CompanyLeaveType;
  allocationMonth?: number | string | null;
  minMonthsOfService?: number | null;
} & Partial<LeaveCycleSettings>;

export interface CompanyLeavePolicy {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  policyScope: PolicyScope;
  isActive: boolean;
  departmentDesignationMappings?:
    DepartmentDesignationMappings | DepartmentDesignationMappingItem[] | null;
  employeeMappings?: EmployeeMappings | null;
  employeeLeaveMappings?: EmployeeLeaveMappingItem[] | null;
  branchMappings?: CompanyLeavePolicyBranchMapping[] | null;
  policyLeaveTypes?: PolicyLeaveTypeWithDetails[];
  editAllowed: boolean;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// Request/Response Types for Leave Policies

export interface CreateCompanyLeavePolicyRequest {
  name: string;
  description?: string;
  effectiveStartDate: string; // YYYY-MM-DD
  effectiveEndDate?: string; // YYYY-MM-DD
  policyScope: PolicyScope;
  isActive: boolean;
  branchIds: string[]; // required, minimum 1 branch UUID
  departmentDesignationMappings?: DepartmentDesignationMappings;
  employeeMappings?: EmployeeMappings;
}

export interface CreateCompanyLeavePolicyResponse {
  message: string;
  data: CompanyLeavePolicy;
}

export interface GetCompanyLeavePoliciesParams {
  companyId: string;
  page?: number;
  limit?: number;
  searchQuery?: string;
  policyScope?: PolicyScope;
  isActive?: boolean;
  branchId?: string;
}

export interface GetCompanyLeavePoliciesResponse {
  message: string;
  data: {
    data: CompanyLeavePolicy[];
    meta: PaginationMeta;
  };
}

export type UpdateCompanyLeavePolicyRequest =
  Partial<CreateCompanyLeavePolicyRequest>;

export interface UpdateCompanyLeavePolicyResponse {
  message: string;
  data: CompanyLeavePolicy;
}

/** Unused days employees still hold under a policy, per leave type. */
export interface PolicyUnusedBalances {
  employeeCount: number;
  unusedDays: number;
  leaveTypes: {
    leaveTypeId: string;
    name: string;
    employeeCount: number;
    unusedDays: number;
  }[];
}

export type GetPolicyUnusedBalancesResponse = ApiResponse<PolicyUnusedBalances>;

// Policy Leave Type Types

export type PolicyLeaveType = {
  id: string;
  policyId: string;
  leaveTypeId: string;
  allocationMonth: string;
  allocationType: AllocationType;
  allowance: number;
  isAutoApproved: boolean;
  isProRated: boolean;
  /** `record_only`: an absence with no balance (spec 2026-10-21). */
  tracking?: LeaveTracking;
  isPaid?: boolean;
  reasonRequired?: boolean;
  /** false: an HR-only type (employees cannot request it). */
  employeeCanRequest?: boolean;
  supportingDocument?: SupportingDocumentRule;
  /** This rule's own approval route; null = the company route (D187). */
  approvalRoute?: ApprovalRoute | null;
  minAdvanceDays: number;
  allocationDate: number; // 1-31
  isCarryForwardEnabled: boolean;
  carryForwardType?: CarryForwardType | null;
  carryForwardMaxDays?: number | null;
  carryForwardPercentage?: number | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  minYearsOfService?: number | null;
  maxYearsOfService?: number | null;
  /** Length-of-service bounds in months; years are the legacy columns. */
  minMonthsOfService?: number | null;
  maxMonthsOfService?: number | null;
  companyLeaveType?: CompanyLeaveType;
} & Partial<LeaveCycleSettings>;

// Request/Response Types for Policy Leave Types

export type CreatePolicyLeaveTypeRequest = {
  leaveTypeId: string;
  allocationType: AllocationType;
  allocationMonth?: number;
  allowance: number;
  isProRated: boolean;
  isAutoApproved: boolean;
  /** Fixed at creation; an update sending another value is a 422. */
  tracking?: LeaveTracking;
  isPaid?: boolean;
  reasonRequired?: boolean;
  /** false: an HR-only type (employees cannot request it). */
  employeeCanRequest?: boolean;
  supportingDocument?: SupportingDocumentRule;
  /** This rule's own approval route; null = the company route (D187). */
  approvalRoute?: ApprovalRoute | null;
  allocationDate: number;
  minAdvanceDays: number;
  isCarryForwardEnabled: boolean;
  carryForwardType?: CarryForwardType;
  carryForwardMaxDays?: number;
  carryForwardPercentage?: number;
  /** Months of service bounds; null clears (no limit). */
  minMonthsOfService?: number | null;
  maxMonthsOfService?: number | null;
  /** Update of a daily accrual rule: also apply to current leave years. */
  applyToCurrentYears?: boolean;
} & Partial<LeaveCycleSettings>;

export interface CreatePolicyLeaveTypeResponse {
  message: string;
  data: PolicyLeaveType;
}

export interface GetPolicyLeaveTypesParams {
  companyId: string;
  policyId: string;
  page?: number;
  limit?: number;
  branchId?: string;
}

export interface GetPolicyLeaveTypesResponse {
  message: string;
  data: {
    data: PolicyLeaveType[];
    meta: PaginationMeta;
  };
}

export type UpdatePolicyLeaveTypeRequest =
  Partial<CreatePolicyLeaveTypeRequest>;

export interface UpdatePolicyLeaveTypeResponse {
  message: string;
  data: PolicyLeaveType;
}

interface PolicyBasedEmployee {
  employee_id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
}
export type GetPolicyBasedEmployeesResponse = ApiResponse<
  PolicyBasedEmployee[]
>;

export interface GrantLeavesRequest {
  policyLeaveType: string;
  recipientScope: GrantRecipientScope;
  employeeIds?: string[];
  branchIds?: string[];
  departmentIds?: string[];
  amount: number;
  comment?: string;
  /** YYYY-MM-DD. After today (company calendar), the grant is scheduled. */
  effectiveDate?: string;
}

/** `POST grant-leaves` with a future `effectiveDate`. */
export interface ScheduledGrantResult {
  scheduled: true;
  scheduledGrant: {
    id: string;
    effectiveDate: string;
    recipientCount: number;
    status: ScheduledGrantStatus;
  };
}

/** One row of `GET grant-leaves/scheduled`. */
export interface ScheduledGrantLeaveItem {
  id: string;
  status: ScheduledGrantStatus;
  /** YYYY-MM-DD */
  effectiveDate: string;
  recipientScope: GrantRecipientScope;
  recipientCount: number;
  departmentCount: number;
  branchCount: number;
  /** Signed days. */
  amount: number;
  type: LeaveGrantType;
  remark: string | null;
  failureReason: ScheduledGrantFailure | null;
  createdAt: string;
  policyLeaveType: { id: string; companyLeaveType: { name: string } };
  createdByUser: { firstName: string; lastName: string };
}

export type GetScheduledGrantLeavesResponse = ApiResponse<
  ScheduledGrantLeaveItem[]
>;

/** A yearly lump sum whose credit date passed before the policy covered it. */
export interface LeaveCatchUpItem {
  policyLeaveTypeId: string;
  leaveTypeName: string;
  allowance: number;
  /** YYYY-MM-DD */
  creditDate: string;
  fullAmount: number;
  proRataAmount: number;
  eligibleCount: number;
  notYetEligibleCount: number;
  alreadyCreditedCount: number;
}

export type GetLeaveCatchUpResponse = ApiResponse<LeaveCatchUpItem[]>;

export interface LeaveCatchUpRequest {
  policyId: string;
  method: CatchUpMethod;
}

export interface DirectGrantLeaveItem {
  id: string;
  amount: number;
  remark: string | null;
  createdAt: string;
  /** YYYY-MM-DD: the day the grant counts from (null on older grants). */
  effectiveDate?: string | null;
  type?: 'grant' | 'deduct';
  /** The grant's ledger entry (what "Reverse" acts on). */
  transactionId?: string;
  /** When the grant was reversed, if it was. */
  reversedAt?: string | null;
  /** This row is the reversal of another grant. */
  isReversal?: boolean;
  /** A manual grant that can still be reversed. */
  reversible?: boolean;

  grantedToEmployee: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
    };
  };

  grantedByUser: {
    firstName: string;
    lastName: string;
  };

  policyLeaveType: {
    id: string;
    companyLeaveType: {
      name: string;
    };
  };
}

export type GetDirectGrantLeavesParams = OptionalPageLimit & {
  branchId?: string;
};

export type GetDirectGrantLeavesResponse =
  GetListResponse<DirectGrantLeaveItem>;

export interface GetPotentialSchedulesRequest {
  effectiveStartDate: Date;
  effectiveEndDate?: Date;
  allocationType: string;
  allocationDate: number;
  allocationMonth: number;
}

export interface GetPotentialSchedulesResponse {
  message: string;
  data: string[];
}
